import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "@/api/query-keys";
import { type UserResponse, usersApi } from "@/api/users";
import { noRetryOn4xx, useQueryErrorToast } from "@/hooks/useQueryErrorToast";

/**
 * Hook to fetch all users for the current organization
 *
 * Fetches merged user data from Kinde + MongoDB via the backend API.
 * Returns user list with loading and error states.
 */
export const useUsers = (enabled: boolean = true) => {
	const {
		data: usersData,
		error: usersError,
		isError: isUsersError,
		isLoading: isLoadingUsers,
		isSuccess: isUsersSuccess,
		refetch: refetchUsers,
	} = useQuery<UserResponse[], Error>({
		enabled,
		queryFn: async () => {
			return await usersApi.getUsers();
		},
		queryKey: queryKeys.users.all,
		retry: noRetryOn4xx,
	});

	useQueryErrorToast({
		error: usersError,
		isError: isUsersError,
		title: "Failed to load users",
	});

	return {
		isLoadingUsers,
		isUsersError,
		isUsersSuccess,
		refetchUsers,
		usersData: usersData ?? [],
		usersError,
	};
};
